import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect } from "../../components/erp";
import { errorText } from "../../api/client";
import { ratesApi, RateHistoryRow } from "./api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function PurchaseRates() {
  const qc = useQueryClient();
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1)); const [year, setYear] = useState(String(now.getFullYear()));
  const years = Array.from({ length: 6 }, (_, i) => String(now.getFullYear() - i));
  const history = useQuery({ queryKey: ["purchase", "rates-history", month, year], queryFn: () => ratesApi.history({ Month: month, Year: year, Type: "VM" }) });

  const [dateId, setDateId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const sheet = useQuery({ queryKey: ["purchase", "rates-sheet", dateId], enabled: creating, queryFn: () => ratesApi.sheet(dateId || undefined) });
  const [rates, setRates] = useState<Record<string, string>>({});
  const [selectDate, setSelectDate] = useState(new Date().toISOString().slice(0, 10));
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [saving, setSaving] = useState(false);

  useEffect(() => {
    const r: Record<string, string> = {};
    (sheet.data?.Data || []).forEach((row) => { r[row.RmCount] = String(row.RmPrice ?? ""); });
    setRates(r);
  }, [sheet.data]);

  const openNew = () => { setDateId(null); setCreating(true); setMsg(null); };
  const openEdit = (row: RateHistoryRow) => { setDateId(String(row.DateID)); setCreating(true); setMsg(null); };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const grades = Object.keys(rates); const values = grades.map((g) => rates[g]);
      const r = await ratesApi.post({ Grades: grades, Rates: values, IsEdit: dateId ? 1 : 0, DateId: dateId, SType: "VM", SelectDate: selectDate });
      if (r.Error) { setMsg({ ok: false, text: r.Error }); } else { setMsg({ ok: true, text: r.Msg || "RM Prices Saved Successfully" }); qc.invalidateQueries({ queryKey: ["purchase", "rates-history"] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Purchase Rates" location="Purchase" />
      <div className="row rowFilter"><div className="col-md-12"><div className="card"><div className="card-body"><div className="row">
        <div className="col col-md-3 mb-3"><ErpSelect id="drpRateMonth" placeholder="Select Month" value={month} onChange={setMonth} options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} /></div>
        <div className="col col-md-3 mb-3"><ErpSelect id="drpRateYear" placeholder="Select Year" value={year} onChange={setYear} options={years.map((y) => ({ value: y, label: y }))} /></div>
        <div className="col col-md-3 mb-3"><button type="button" className="btn btn-success" onClick={openNew} data-testid="btn-new-rate-sheet">New Rate Sheet</button></div>
      </div></div></div></div></div>
      <div className="card"><div className="card-body">
        {history.isLoading && <div><span className="erp-spinner" /> Loading...</div>}
        {history.isError && <Alert kind="danger">{errorText(history.error)}</Alert>}
        <ul className="list-group" data-testid="rate-history-list">
          {(history.data?.Data || []).map((row) => (
            <li key={row.DateID} className="list-group-item d-flex justify-content-between align-items-center" data-testid={`rate-history-${row.DateID}`}>
              Rates From - {row.RmPriceDate}
              <button type="button" className="btn btn-link p-0 text-primary" onClick={() => openEdit(row)} data-testid={`edit-rate-${row.DateID}`}><i className="mdi mdi-pencil font-size-18" /></button>
            </li>
          ))}
          {!history.isLoading && (history.data?.Data || []).length === 0 && <li className="list-group-item text-muted">No rate sheets for this month</li>}
        </ul>
      </div></div>
      {creating && (
        <div className="card"><div className="card-body">
          <h6>{dateId ? "Edit Rate Sheet" : "New Rate Sheet"}</h6>
          {!dateId && <div className="mb-3" style={{ maxWidth: 220 }}><label className="form-label">Rates From</label><input type="date" className="form-control" value={selectDate} onChange={(e) => setSelectDate(e.target.value)} data-testid="rate-select-date" /></div>}
          {sheet.isLoading && <div><span className="erp-spinner" /> Loading grades...</div>}
          <div className="row" data-testid="rate-grades-grid">
            {Object.keys(rates).map((grade) => (
              <div className="col-md-2 mb-2" key={grade}>
                <label className="form-label" style={{ fontSize: 11 }}>{grade}</label>
                <input className="form-control form-control-sm" value={rates[grade]} onChange={(e) => setRates((s) => ({ ...s, [grade]: e.target.value }))} data-testid={`rate-input-${grade}`} />
              </div>
            ))}
          </div>
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="rate-save-msg">{msg.text}</Alert>}
          <div className="d-flex gap-2 mt-2">
            <button type="button" className="btn btn-primary" disabled={saving} onClick={save} data-testid="rate-save-btn">{saving ? "Saving..." : "Save Rates"}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setCreating(false)} data-testid="rate-cancel-btn">Cancel</button>
          </div>
        </div></div>
      )}
    </>
  );
}
